import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { CandidateDetailPanel } from './TmCandidatesTab';

const DeviceTmCandidatesView = () => {
  const { serial: routeSerial } = useParams();
  const [tmReport, setTmReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const device = useMemo(
    () => ({
      serial_number: routeSerial?.toLowerCase(),
      name: routeSerial,
    }),
    [routeSerial],
  );

  const fetchTmReport = useCallback(async () => {
    if (!routeSerial) {
      setError('No device serial specified');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const serial = routeSerial.toLowerCase();
      const response = await api.getTmReport(serial);
      setTmReport(response || { candidates: [] });
    } catch (err) {
      console.error('Error fetching TM candidates report:', err);
      setError(err.message || 'Failed to fetch TM candidates report');
      setTmReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [routeSerial]);

  useEffect(() => {
    if (routeSerial) {
      fetchTmReport();
    }
  }, [routeSerial, fetchTmReport]);

  if (!routeSerial) {
    return (
      <Paper elevation={3} sx={{ p: 3, m: 2 }}>
        <Alert severity="error">No device serial specified in the URL.</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            TM Candidates Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Device: <strong>{routeSerial}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={fetchTmReport}
          disabled={isLoading}
        >
          {isLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && !tmReport && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading TM candidates…</Typography>
        </Box>
      )}

      {tmReport && (
        <>
          <CandidateDetailPanel device={device} data={tmReport} deviceLookup={new Map()} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.open(`/device/${routeSerial}`, '_blank')}
            >
              View Device Details
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default DeviceTmCandidatesView;
